import { AdminGroupHierarchyNode } from "@nextiva/next-ui-wayfinder";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { getAdminGroupsHierarchy } from "api/adminGroupService";

export const useGetAdminGroupHierarchy = ({
  corpAccountNbr,
  isLbaEnabled = true,
}: {
  corpAccountNbr: string;
  isLbaEnabled?: boolean;
}): UseQueryResult<AdminGroupHierarchyNode, Error> & {
  /**
   * True from the first failed fetch until a fetch succeeds. Use this (not
   * isError) for the breadcrumb/scope disabled state: when a retry starts on
   * a query that has no data, react-query clears the error and flips status
   * back to "loading", so isError blinks false for the whole round trip.
   * Observers mounting on route changes trigger exactly such retries, which
   * made the disabled state (and the suppressed scope) flicker on every
   * navigation while the endpoint was down.
   */
  isFailed: boolean;
} => {
  const query = useQuery<AdminGroupHierarchyNode, Error>({
    queryKey: ["adminGroupHierarchy", corpAccountNbr],
    queryFn: () => getAdminGroupsHierarchy(),
    enabled: isLbaEnabled && Boolean(corpAccountNbr),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    isFailed: query.isError || (query.isLoading && query.errorUpdateCount > 0),
  };
};
